// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { Modal } from './Modal';
import './RejectionReasonModal.css';

export interface RejectionReasonModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Human-readable rejection reason (the resolved label of the provider's
   * rejection reason code, e.g. "Address does not match").
   */
  reason?: string | null;
  /** Free-text additional information supplied by the provider. */
  additionalInformation?: string | null;
  /**
   * Document the rejection applies to. Drives the first field label
   * ("Reason for {documentLabel} rejection"). Defaults to "Letter of Authority".
   */
  documentLabel?: string;
}

/**
 * Read-only modal that displays the justification a provider gave when
 * rejecting an LOA/CoA on the Open Access (provider landing) page. Wraps the
 * standardised {@link Modal}; the header icon ("rejection alert") gets the
 * shared 8px gap to the title from `.fin-modal__header-left`.
 */
export function RejectionReasonModal({
  open,
  onClose,
  reason,
  additionalInformation,
  documentLabel = 'Letter of Authority',
}: RejectionReasonModalProps) {
  return (
    <Modal open={open} onClose={onClose} size="medium" className="fin-rejection-modal">
      <Modal.Header icon="rejection alert" title="Rejection Reason" onClose={onClose} />
      <Modal.Content>
        <div className="fin-rejection-modal__field">
          <span className="fin-rejection-modal__label">
            Reason for {documentLabel} rejection.
            <span className="fin-rejection-modal__required">*</span>
          </span>
          <div className="fin-rejection-modal__value">
            <span className="fin-rejection-modal__text">{reason || '—'}</span>
          </div>
        </div>
        <div className="fin-rejection-modal__field">
          <span className="fin-rejection-modal__label">Additional information</span>
          <div className="fin-rejection-modal__value fin-rejection-modal__value--multiline">
            <span className="fin-rejection-modal__text">{additionalInformation || '—'}</span>
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions primaryAction={{ label: 'Close', onClick: onClose }} />
    </Modal>
  );
}
